/**
 * Chat Header Component
 * Contains model selector, character selector, controls, and rate limit display
 */

import { ColorSchemeToggle } from '@gofranz/common-components';
import { ActionIcon, Box, Divider, Group, Menu } from '@mantine/core';
import { IconMenu2, IconUser } from '@tabler/icons-react';
import React from 'react';
import type { ChatHeaderProps } from '../../types/chat';
import { LanguageSelector } from '../LanguageSelector/LanguageSelector';
import { AccountNavigation } from '../Layout/AccountNavigation';
import { ContextUsageIndicator } from './ContextUsageIndicator';
import { NewChatButton } from './NewChatButton';
import { RateLimitBadge } from './RateLimitBadge';

export const ChatHeader = React.memo<ChatHeaderProps>(
  ({
    onNewChat,
    onToggleSidebar,
    rateLimitInfo,
    isLoggedIn,
    isDesktop,
    models,
    selectedModel,
    selectedCharacter,
    characters,
    currentTokenUsage,
    contextWindow,
    onNewChatConfirm,
  }) => {
    return (
      <Box
        p="md"
        style={{
          borderBottom: `1px solid var(--mantine-color-default-border)`,
          backgroundColor: `var(--mantine-color-default)`,
          zIndex: 10,
        }}
      >
        <Group justify="space-between" align="center">
          <Group align="center" gap="md">
            <ActionIcon
              variant="subtle"
              color="gray"
              onClick={onToggleSidebar}
              aria-label="Toggle sidebar"
            >
              <IconMenu2 size={18} />
            </ActionIcon>

            <NewChatButton
              onClick={onNewChat}
              disabled={false}
              isDesktop={isDesktop}
              models={models}
              characters={characters}
              selectedModel={selectedModel}
              selectedCharacter={selectedCharacter}
              isLoggedIn={isLoggedIn}
              onNewChatConfirm={onNewChatConfirm}
            />

            <RateLimitBadge
              rateLimitInfo={rateLimitInfo!}
              isLoggedIn={isLoggedIn}
              isDesktop={isDesktop}
            />

            {currentTokenUsage && contextWindow && contextWindow > 0 && (
              <ContextUsageIndicator
                currentTokens={currentTokenUsage.total_tokens}
                contextLimit={contextWindow}
                lastResponseTokens={currentTokenUsage.completion_tokens}
                compact={!isDesktop}
              />
            )}
          </Group>

          <Group gap="md" align="center">
            {isLoggedIn && (
              <Menu shadow="md" width={250}>
                <Menu.Target>
                  <ActionIcon variant="subtle" color="gray" aria-label="Account">
                    <IconUser size={18} />
                  </ActionIcon>
                </Menu.Target>

                <Menu.Dropdown>
                  <Box p="xs">
                    <Group justify="space-between" mb="sm">
                      <LanguageSelector />
                      <ColorSchemeToggle />
                    </Group>
                    <Divider mb="xs" />
                    <AccountNavigation />
                  </Box>
                </Menu.Dropdown>
              </Menu>
            )}
          </Group>
        </Group>
      </Box>
    );
  }
);

ChatHeader.displayName = 'ChatHeader';
